import type { AuditEvent, ItemStatus } from '../../domain/inventory';
import { statusFamilies, type StatusFamily } from '../../domain/status';

// The seven real lifecycle families, in flow order. STATUS_UNSPECIFIED /
// data-quality is deliberately excluded — SPEC.md §6 treats it as a data
// defect, not a lifecycle stage, so it never appears on the rail.
export const lifecycleFamilyOrder: StatusFamily[] = [
  'procurement',
  'logistics',
  'deployed',
  'service',
  'return',
  'storage',
  'end-of-life',
];

export interface LifecycleSegment {
  family: StatusFamily;
  observed: boolean;
  current: boolean;
}

export function statusFromPayload(payload: unknown): ItemStatus | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const record: Record<string, unknown> = payload as Record<string, unknown>;
  const data: Record<string, unknown> =
    record.data && typeof record.data === 'object' ? (record.data as Record<string, unknown>) : record;
  return typeof data.status === 'string' ? (data.status as ItemStatus) : undefined;
}

/**
 * "Observed" families come from the audit log — this rail describes what has
 * happened, never a next step (design_handoff §1e option B: "no transition
 * rules approved").
 */
export function computeObservedFamilies(history: AuditEvent[], currentStatus: ItemStatus | undefined): LifecycleSegment[] {
  const observed = new Set<StatusFamily>();
  for (const event of history) {
    for (const payload of [event.old, event.new]) {
      const status = statusFromPayload(payload);
      if (status) observed.add(statusFamilies[status]);
    }
  }
  const currentFamily = statusFamilies[currentStatus ?? 'STATUS_UNSPECIFIED'];
  if (currentFamily !== 'data-quality') observed.add(currentFamily);

  return lifecycleFamilyOrder.map((family) => ({
    family,
    observed: observed.has(family),
    current: family === currentFamily,
  }));
}
